/**
 * Global site chrome, company details, SEO strings, and the microcopy inventory
 * (plan/05 §7, plan/08 §2). One place so wording never diverges between nav,
 * footer, meta, and forms. Legal details are sourced from the Estonian
 * e-Business Register (registry code 16025775).
 */
import { pricing } from './pricing';

export const site = {
	name: 'PPF Engine',
	/** Footer tagline (plan/05 §7). */
	tagline: 'Precision film templates, from your own scans. Made in Estonia.',
	domain: 'ppfengine.com', // D1 placeholder
	contactEmail: 'hello@ppfengine.com', // routes to a real human inbox
	url: 'https://ppfengine.com' // D1 — PUBLIC_SITE_URL is the runtime source
} as const;

/** Checkout trust marks shown in the footer (plan/04 §08). All three brands are
    accepted through Creem's hosted checkout (merchant of record). */
export const trust = {
	label: 'Secure checkout',
	cards: ['visa', 'mastercard', 'amex'] as const
} as const;

/** Company / imprint block (plan/04 §08, plan/09 §3). */
export const company = {
	legalName: 'Andify OÜ',
	registryCode: '16025775',
	address: 'Luha tn 2-6, 50703 Tartu, Estonia',
	vatNumber: 'EE102540811'
} as const;

/** Slim sticky nav (plan/04 §00). Anchors target homepage sections.
    CTA is the live-site wording — "Presale" with the "Save 55%" offer as a chip. */
export const nav = {
	links: [
		{ label: 'Product', href: '/#process' },
		{ label: 'Proof', href: '/#proof' },
		{ label: 'Pricing', href: '/#pricing' },
		{ label: 'FAQ', href: '/#faq' }
	],
	cta: { label: 'Presale', offer: `Save ${pricing.discountPct}%`, href: '/#pricing' }
} as const;

/** Footer links (plan/04 §08). */
export const footerLinks = [
	{ label: 'PPF pattern software', href: '/ppf-pattern-software' },
	{ label: 'How to make PPF patterns', href: '/guides/how-to-make-custom-ppf-patterns' },
	{ label: '3D scan to PPF pattern', href: '/technology/3d-scan-to-ppf-pattern' },
	{ label: 'Case study: RAM 1500 flare', href: '/case-studies/ram-1500-fender-flare' },
	{ label: 'Case study: BMW X3 30e', href: '/case-studies/bmw-x3-30e-fender-charging-cap' },
	{ label: 'Scanner and plotter compatibility', href: '/technology/scanner-plotter-compatibility' },
	{ label: 'PPF Engine vs ExactFlat', href: '/compare/ppf-engine-vs-exactflat' },
	{ label: 'Pricing', href: '/pricing' },
	{ label: 'FAQ', href: '/faq' },
	{ label: 'Pre-order terms', href: '/legal/preorder' },
	{ label: 'Privacy', href: '/legal/privacy' },
	{ label: 'Terms', href: '/legal/terms' },
	{ label: 'Imprint', href: '/legal/imprint' }
] as const;

/** The ticker strip between hero and story — the output path and the promise,
    in the readout voice. Kept short: a marquee is a rhythm, not a paragraph. */
export const marquee = [
	'SVG',
	'DXF',
	'PLT',
	'Graphtec FC9000',
	'Any car',
	'Any panel',
	'No catalog wait'
] as const;

/** Default + homepage SEO strings (plan/08 §2). */
export const meta = {
	titlePattern: (page: string) => `${page}: PPF Engine`,
	homeTitle: 'PPF Engine: Create the PPF patterns your library doesn’t have',
	homeDescription: `Create the PPF pattern your library doesn’t have, from your own 3D scan. Plotter-ready SVG, DXF and PLT. Request a workflow demo or pre-order at ${pricing.discountPct}% off.`,
	themeColor: '#FCFCFA'
} as const;

/** Microcopy inventory (plan/05 §7). */
export const microcopy = {
	buttons: {
		processing: 'Processing…',
		tryAgain: 'Try again',
		adding: 'Adding…'
	},
	validation: {
		emailIncomplete: 'That email looks incomplete.'
	},
	waitlist: {
		success: 'You’re on the list.',
		prompt: 'Not ready? Get the launch note.'
	},
	checkoutCancelled: 'Checkout cancelled. Your card was not charged.',
	error404: 'This page didn’t survive the cut.',
	madeIn: 'Made in Estonia',
	studioLine: 'Built inside a working PPF studio.'
} as const;
